import { useState } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover'
import { Settings2Icon } from 'lucide-react'
import {
  type ImageResolution,
  getAvailableResolutions,
} from '@/components/workspace/workspace-editor/workspace-editor-popup/types'
import { type ImageModel } from '@backend/lib/ai/functions/generateImage'

interface Props {
  resolution: ImageResolution
  onResolutionChange: (value: ImageResolution) => void
  enhance?: boolean
  onEnhanceChange?: (value: boolean) => void
  useCaption: boolean
  onUseCaptionChange: (value: boolean) => void
  useColorPalette: boolean
  onUseColorPaletteChange: (value: boolean) => void
  imageModel?: ImageModel
  // Whether a brand color source exists (a moodboard with a palette).
  // Gates the "Inject brand colors" toggle. Name kept for back-compat across the
  // popup → image-prompt-box → settings prop chain.
  hasBrandColors?: boolean
  disabled?: boolean
  showCaptionAndColors?: boolean
  flexSteps?: number
  onFlexStepsChange?: (steps: number) => void
  flexGuidance?: number
  onFlexGuidanceChange?: (guidance: number) => void
}

export function ImageGenerationSettings({
  resolution,
  onResolutionChange,
  enhance,
  onEnhanceChange,
  useCaption,
  onUseCaptionChange,
  useColorPalette,
  onUseColorPaletteChange,
  imageModel,
  hasBrandColors = true,
  disabled,
  showCaptionAndColors = true,
  flexSteps,
  onFlexStepsChange,
  flexGuidance,
  onFlexGuidanceChange,
}: Props) {
  const [open, setOpen] = useState(false)
  const availableResolutions = getAvailableResolutions(imageModel)
  const showFlexParams = imageModel === 'flux-2-flex' && onFlexStepsChange && onFlexGuidanceChange

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" disabled={disabled}>
          <Settings2Icon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end" side="top" sideOffset={8}>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-foreground text-xs font-medium">Resolution</span>
            <div className="flex items-center gap-0.5">
              {availableResolutions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => onResolutionChange(option)}
                  disabled={disabled}
                  className={`flex-1 rounded px-2 py-1 text-xs transition-colors ${
                    resolution === option
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground/60 hover:text-foreground hover:bg-hover'
                  }`}>
                  {option}
                </button>
              ))}
            </div>
          </div>

          {showFlexParams && (
            <>
              <div className="bg-border h-px" />
              <div className="flex flex-col gap-2">
                <FlexParamSlider
                  label="Steps"
                  value={flexSteps ?? 50}
                  defaultValue={50}
                  min={1}
                  max={50}
                  step={1}
                  onChange={onFlexStepsChange}
                  disabled={disabled}
                />
                <FlexParamSlider
                  label="Guidance"
                  value={flexGuidance ?? 5}
                  defaultValue={5}
                  min={1.5}
                  max={10}
                  step={0.5}
                  onChange={onFlexGuidanceChange}
                  disabled={disabled}
                />
              </div>
            </>
          )}

          <div className="bg-border h-px" />

          <div className="flex flex-col gap-2">
            {onEnhanceChange && (
              <label className="flex items-center justify-between">
                <span className="text-foreground text-xs font-medium">Prompt enhancer</span>
                <input
                  type="checkbox"
                  checked={enhance}
                  onChange={(e) => onEnhanceChange(e.target.checked)}
                  disabled={disabled}
                  className="accent-current"
                />
              </label>
            )}

            {showCaptionAndColors && (
              <div className={`flex flex-col gap-2 ${onEnhanceChange ? 'pl-3' : ''}`}>
                <label className="flex items-center justify-between">
                  <span
                    className={`text-xs ${enhance !== false ? 'text-foreground/70' : 'text-foreground/30'}`}>
                    Inject caption
                  </span>
                  <input
                    type="checkbox"
                    checked={useCaption}
                    onChange={(e) => onUseCaptionChange(e.target.checked)}
                    disabled={disabled || enhance === false}
                    className="accent-current"
                  />
                </label>
                <label className="flex items-center justify-between">
                  <span
                    className={`text-xs ${enhance !== false && hasBrandColors ? 'text-foreground/70' : 'text-foreground/30'}`}>
                    Inject brand colors
                  </span>
                  <input
                    type="checkbox"
                    checked={useColorPalette}
                    onChange={(e) => onUseColorPaletteChange(e.target.checked)}
                    disabled={disabled || enhance === false || !hasBrandColors}
                    className="accent-current"
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function FlexParamSlider({
  label,
  value,
  defaultValue,
  min,
  max,
  step = 1,
  onChange,
  disabled,
}: {
  label: string
  value: number
  defaultValue: number
  min: number
  max: number
  step?: number
  onChange: (value: number) => void
  disabled?: boolean
}) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-foreground/70 w-14 text-xs">{label}</label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        className="h-1.5 flex-1 cursor-pointer accent-current"
      />
      <button
        type="button"
        onClick={() => onChange(defaultValue)}
        disabled={disabled}
        className="text-foreground/50 hover:text-foreground w-8 text-right text-xs tabular-nums"
        title={`Reset to ${defaultValue}`}>
        {value}
      </button>
    </div>
  )
}
